import React, { useId } from "react";
import { Controller, Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import AppText, { FormLabel } from "../AppText/AppText";

export interface RadioOption {
	/** Value submitted/reported when this option is selected. */
	value: string;
	/** Visible label. Treated as an i18n key unless `skipTranslation` is set. */
	label: string;
	/** Optional muted helper text beneath the label (i18n key unless skipped). */
	hint?: string;
	/** Disable just this option. */
	disabled?: boolean;
}

export interface RadioGroupProps {
	/** The options to render as radio buttons. */
	options: RadioOption[];
	/**
	 * Shared `name` for the underlying radio inputs. Falls back to a generated
	 * id so multiple groups on the page don't bleed into each other.
	 */
	name?: string;
	/** Controlled selected value. Leave undefined for uncontrolled (`defaultValue`). */
	value?: string;
	defaultValue?: string;
	onChange?: (value: string) => void;
	/** Group label (i18n key unless `skipTranslation`). */
	label?: string;
	/** Adds an asterisk next to the label. No validation logic attached. */
	mandatory?: boolean;
	/** Disable the whole group. */
	disabled?: boolean;
	/** Error message (i18n key unless `skipTranslation`) shown in red beneath the group. */
	errors?: string;
	/** Lay options out horizontally (default) or stacked vertically. */
	orientation?: "horizontal" | "vertical";
	/** Skip i18n translation of label/option labels/hints/errors. */
	skipTranslation?: boolean;
	/** Optional tooltip ℹ️ on the group label. Mutually exclusive with `knowledgeBaseTag`. */
	tooltip?: string | React.ReactNode;
	/** Hjälpcenter tag; shows a help icon on the group label on hover. */
	knowledgeBaseTag?: string;
	/** Custom className for the outer container. */
	containerClassName?: string;
	/** Custom className for the options wrapper. */
	wrapperClassName?: string;
	/** Custom className for each option's label. */
	optionLabelClassName?: string;
}

const RadioGroup = React.forwardRef<HTMLInputElement, RadioGroupProps>(
	(
		{
			options,
			name,
			value,
			defaultValue,
			onChange,
			label,
			mandatory,
			disabled,
			errors,
			orientation = "horizontal",
			skipTranslation = false,
			tooltip,
			knowledgeBaseTag,
			containerClassName,
			wrapperClassName,
			optionLabelClassName,
		},
		ref
	) => {
		const { t } = useTranslation();
		const generatedName = useId();
		const groupName = name ?? generatedName;

		const tr = (key?: string) => (key ? (skipTranslation ? key : t(key as any)) : "");

		function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
			onChange?.(e.target.value);
		}

		return (
			<div className={cn("w-full", containerClassName)}>
				{label &&
					(knowledgeBaseTag ? (
						<FormLabel showKnowledgeBaseForTagOnHover={knowledgeBaseTag}>
							{tr(label)}
							{mandatory ? " *" : ""}
						</FormLabel>
					) : (
						<FormLabel tooltip={tooltip}>
							{tr(label)}
							{mandatory ? " *" : ""}
						</FormLabel>
					))}

				<div
					role="radiogroup"
					className={cn(
						"flex select-none gap-x-5 gap-y-2.5",
						orientation === "vertical" ? "flex-col" : "flex-row flex-wrap items-start",
						wrapperClassName
					)}
				>
					{options.map((option, index) => {
						const optDisabled = disabled || option.disabled;
						const isFirst = index === 0;

						return (
							<label
								key={option.value}
								className={cn(
									"group flex items-start gap-2",
									optDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
								)}
							>
								{/* Native input drives state/accessibility; visually hidden. */}
								<input
									// Forward the ref to the first input so RHF/focus lands on the group.
									ref={isFirst ? ref : undefined}
									type="radio"
									name={groupName}
									value={option.value}
									checked={value !== undefined ? value === option.value : undefined}
									defaultChecked={
										value === undefined && defaultValue !== undefined
											? defaultValue === option.value
											: undefined
									}
									disabled={optDisabled}
									onChange={handleChange}
									className="peer sr-only"
								/>
								<span
									aria-hidden="true"
									className={cn(
										"mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
										"border border-gray-300 bg-white transition-colors",
										"peer-checked:border-brand peer-checked:[&>span]:bg-brand",
										"peer-focus-visible:ring-2 peer-focus-visible:ring-brand/40 peer-focus-visible:ring-offset-1",
										!optDisabled && "group-hover:border-brand",
										errors && "border-red-500"
									)}
								>
									{/* Inner dot is transparent until the input is checked
									    (filled via the box span's peer-checked child variant). */}
									<span className="h-2 w-2 rounded-full bg-transparent transition-colors" />
								</span>

								<span className="flex flex-col">
									<span
										className={cn(
											"text-sm font-medium leading-tight text-gray-800 transition-colors",
											!optDisabled && "group-hover:text-gray-900",
											optionLabelClassName
										)}
									>
										{tr(option.label)}
									</span>
									{option.hint && (
										<span className="mt-0.5 text-xs leading-snug text-gray-500">
											{tr(option.hint)}
										</span>
									)}
								</span>
							</label>
						);
					})}
				</div>

				{errors && (
					<AppText margin="0.35rem 0 0 0.15rem" size="xs" color="danger">
						{tr(errors)}
					</AppText>
				)}
			</div>
		);
	}
);

RadioGroup.displayName = "RadioGroup";

export default RadioGroup;

/**
 * React Hook Form wrapper for {@link RadioGroup}.
 *
 * Threads `field` props through and calls both RHF's `onChange` and any
 * external `onChange`, mirroring the `AppSelectController` pattern.
 */
export interface RadioGroupControllerProps extends Omit<RadioGroupProps, "value"> {
	control: Control<any>;
	name: string;
	rules?: object;
}

export const RadioGroupController: React.FC<RadioGroupControllerProps> = ({
	control,
	name,
	rules,
	onChange: onChangeExternal,
	...rest
}) => {
	return (
		<Controller
			control={control}
			name={name}
			rules={rules}
			render={({ field: { value, onChange, ref }, fieldState }) => (
				<RadioGroup
					{...rest}
					ref={ref}
					name={name}
					value={value ?? ""}
					onChange={(val) => {
						onChange(val);
						onChangeExternal?.(val);
					}}
					errors={rest.errors ?? (fieldState.error?.message as string | undefined)}
				/>
			)}
		/>
	);
};
